import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDataProvider } from 'react-admin';
import { storeNotesBatch, setNotesLoading } from '../redux/actions/notes';
import { CustomReducerState } from '../redux/reducers';
import { MC_NOTES } from '../providers/resources';

// Global batch queue to collect requests across components
interface BatchRequest {
    tag: string;
    itemIdsMap: Map<string, Set<string>>; // Map of subscriber key -> Set of itemIds for that subscriber
    allItemIds: Set<string>; // All unique itemIds across all subscribers
    subscribers: Map<string, { itemIds: string[], callback: () => void }>;
    timeoutId?: NodeJS.Timeout;
}

const batchQueue = new Map<string, BatchRequest>();
const BATCH_DELAY = 50; // 50ms debounce time

// Helper to create a cache key
const createCacheKey = (tag: string, itemIds: string[]): string => {
    return `${tag}_${[...itemIds].sort().join(',')}`;
};

export const useNotesBatch = (
    tag: string,
    itemIds: string[]
): any[] | null => {
    console.log('🔧 useNotesBatch called with:', { tag, itemIds });
    const dispatch = useDispatch();
    const dataProvider = useDataProvider();
    const [hasRequested, setHasRequested] = useState(false);
    const subscriberRef = useRef<(() => void) | null>(null);
    const subscriberKeyRef = useRef<string>('');
    
    // Create cache key for this specific request
    const cacheKey = createCacheKey(tag, itemIds);
    
    // Get cached data from Redux
    const cachedData = useSelector((state: CustomReducerState) => {
        return state.rikutec?.notes?.cache?.[cacheKey] || null;
    });
    
    const isLoading = useSelector((state: CustomReducerState) => {
        return state.rikutec?.notes?.loading?.[cacheKey] || false;
    });

    // Create subscriber callback
    const createSubscriber = useCallback(() => {
        return () => {
            // This will be called when batch data is ready
            setHasRequested(true);
        };
    }, []);

    // Register for batch request
    useEffect(() => {
        if (!tag || !itemIds || itemIds.length === 0) return;
        
        // If we already have cached data, don't request again
        if (cachedData) {
            return;
        }
        
        // If already requested in this session, don't request again
        if (hasRequested) {
            return;
        }

        // Generate unique subscriber key for this component instance
        const subscriberKey = `${cacheKey}_${Math.random()}`;
        subscriberKeyRef.current = subscriberKey;

        // Get or create batch request for this tag
        let batchRequest = batchQueue.get(tag);
        if (!batchRequest) {
            batchRequest = {
                tag,
                itemIdsMap: new Map(),
                allItemIds: new Set(),
                subscribers: new Map()
            };
            batchQueue.set(tag, batchRequest);
        }

        // Add this subscriber's itemIds to the batch
        const itemIdsSet = new Set(itemIds);
        batchRequest.itemIdsMap.set(subscriberKey, itemIdsSet);
        
        // Add all itemIds to the global set
        itemIds.forEach(id => batchRequest!.allItemIds.add(id));
        
        // Create and store subscriber
        const subscriber = createSubscriber();
        subscriberRef.current = subscriber;
        batchRequest.subscribers.set(subscriberKey, {
            itemIds: itemIds,
            callback: subscriber
        });

        // Clear existing timeout
        if (batchRequest.timeoutId) {
            clearTimeout(batchRequest.timeoutId);
        }

        // Set new timeout to execute batch
        batchRequest.timeoutId = setTimeout(async () => {
            try {
                const currentBatchRequest = batchQueue.get(tag);
                if (!currentBatchRequest) return;
                
                console.log('🚀 Executing notes batch request for tag:', tag, 'with', currentBatchRequest.allItemIds.size, 'unique itemIds');
                
                // Mark all cache keys as loading
                currentBatchRequest.subscribers.forEach((sub, key) => {
                    const subCacheKey = createCacheKey(tag, sub.itemIds);
                    dispatch(setNotesLoading.fn(subCacheKey, true));
                });

                // Prepare batch request parameters with all unique itemIds
                const allItemIdsArray = Array.from(currentBatchRequest.allItemIds);
                
                // Make batch request
                console.log('📡 Making batch request with filter:', { globalNote_tags: [tag], itemId: allItemIdsArray });
                const response = await dataProvider.getList(MC_NOTES, {
                    pagination: { page: 1, perPage: 100 }, // Increase perPage to get more results
                    sort: { field: 'createdAt', order: 'DESC' },
                    filter: {
                        globalNote_tags: [tag],
                        itemId: allItemIdsArray
                    }
                });
                console.log('✅ Batch response received:', response);

                // Store results for each subscriber based on their specific itemIds
                if (response?.data && Array.isArray(response.data)) {
                    currentBatchRequest.subscribers.forEach((sub, key) => {
                        const subCacheKey = createCacheKey(tag, sub.itemIds);
                        const subItemIdsSet = new Set(sub.itemIds);
                        
                        // Filter notes that match this subscriber's itemIds
                        const filteredNotes = response.data.filter((note: any) => {
                            // Extract itemId from the note - adjust this based on your API response structure
                            const noteItemId = note.itemId || note.item?.id || note.item;
                            if (typeof noteItemId === 'string') {
                                return subItemIdsSet.has(noteItemId);
                            }
                            return false;
                        });
                        
                        // Store filtered results for this specific subscriber
                        dispatch(storeNotesBatch.fn(subCacheKey, filteredNotes.slice(0, 1))); // Only keep the most recent note (perPage: 1)
                    });
                }

                // Notify all subscribers
                currentBatchRequest.subscribers.forEach(sub => {
                    try {
                        sub.callback();
                    } catch (error) {
                        console.warn('Error notifying notes batch subscriber:', error);
                    }
                });

            } catch (error) {
                console.error('Error fetching notes batch:', error);
                // Mark all as not loading on error
                const currentBatchRequest = batchQueue.get(tag);
                if (currentBatchRequest) {
                    currentBatchRequest.subscribers.forEach((sub) => {
                        const subCacheKey = createCacheKey(tag, sub.itemIds);
                        dispatch(setNotesLoading.fn(subCacheKey, false));
                    });
                }
            } finally {
                // Clean up
                batchQueue.delete(tag);
                setHasRequested(true);
            }
        }, BATCH_DELAY);

        // Cleanup function
        return () => {
            if (batchRequest?.timeoutId) {
                clearTimeout(batchRequest.timeoutId);
            }
            if (subscriberKeyRef.current && batchRequest?.subscribers) {
                batchRequest.subscribers.delete(subscriberKeyRef.current);
                batchRequest.itemIdsMap.delete(subscriberKeyRef.current);
            }
        };
    }, [tag, itemIds, cachedData, hasRequested, dispatch, dataProvider, createSubscriber, cacheKey]);

    // Return cached data if available, or null if still loading
    if (cachedData) {
        return cachedData;
    }
    
    // If we're loading or have requested, return null to show loading state
    if (isLoading || hasRequested) {
        return null;
    }
    
    return null;
};

